/* global SideDrawer, trapFocus */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      if (this.listeners) return;
      this.scope = this.closest('.shopify-section') || document;
      this.form = this.querySelector('#facets');
      this.results = this.scope.querySelector('#filter-results');
      this.toolbar = this.scope.querySelector('products-toolbar');
      this.sortSelect = this.scope.querySelector('#products-sort-by');
      this.chips = this.scope.querySelector('#products-active-filters');
      this.content = this.querySelector('.drawer__content');
      this.resultsButton = this.querySelector('.facets__show-results');
      this.error = this.querySelector('.facets__error');
      this.retryButton = this.querySelector('.facets__retry');
      this.toolbarError = this.scope.querySelector('.products-toolbar__error');
      this.pagination = this.scope.querySelector('custom-pagination');
      this.inert = true;
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-hidden', 'true');
      this.expanded = new Set();
      this.requestVersion = 0;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.scope.addEventListener('click', this.handleScopeClick.bind(this), options);
      this.scope.addEventListener('input', this.handleInput.bind(this), options);
      this.scope.addEventListener('change', this.handleInput.bind(this), options);
      this.addEventListener('click', this.handleFilterActions.bind(this), options);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.applyFilters(this.formParams());
      }, options);
      window.addEventListener('popstate', () => {
        this.applyFilters(new URLSearchParams(window.location.search), false);
      }, options);

      this.updateClearLinks();
    }

    disconnectedCallback() {
      clearTimeout(this.filterChangeTimeout);
      this.requestVersion += 1;
      this.request?.abort();
      this.listeners?.abort();
      this.listeners = null;
      if (this.hasAttribute('open')) this.close();
    }

    open(opener) {
      if (this.hasAttribute('open')) return;
      this.inert = false;
      this.overlay.classList.add('overlay--facets');
      super.open(opener, this.querySelector('.drawer__close-btn'));
      document.body.classList.add('facets-open');
      this.backgroundElements = new Map();
      for (let node = this; node.parentElement && node !== document.body; node = node.parentElement) {
        Array.from(node.parentElement.children).forEach((sibling) => {
          if (sibling === node || sibling === this.overlay || sibling.contains(this.overlay)) return;
          if (['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
          // Lock theme content, not body-level app portals. Klaviyo and CookieYes
          // can mount a dialog into an initially empty host while filters are open.
          // Making that host inert also disables the popup's own close button.
          if (node.parentElement === document.body && !sibling.matches('main, header, footer, .shopify-section')) return;
          this.backgroundElements.set(sibling, sibling.inert);
          sibling.inert = true;
        });
      }
      opener?.setAttribute('aria-expanded', 'true');
      this.updateErrorVisibility();
    }

    close() {
      if (!this.hasAttribute('open')) return;
      const opener = this.opener;
      this.backgroundElements?.forEach((inert, element) => { element.inert = inert; });
      this.backgroundElements?.clear();
      document.body.classList.remove('facets-open');
      super.close();
      this.inert = true;
      opener?.setAttribute('aria-expanded', 'false');
      this.overlay.classList.remove('overlay--facets');
      this.updateErrorVisibility();
    }

    // SideDrawer calls this for both the drawer and its shared backdrop.
    handleClick(event) {
      const closeButton = event.target.closest('.js-close-drawer');
      if (closeButton === this.resultsButton && (this.busy || this.failed)) return;
      if (event.target === this.overlay || closeButton) this.close();
    }

    handleScopeClick(event) {
      if (this.contains(event.target)) return;
      const opener = event.target.closest('.products-toolbar__filter-btn');
      if (opener && this.scope.contains(opener)) {
        this.open(opener);
        return;
      }
      if (event.target.closest('.products-toolbar__retry')) {
        this.applyFilters(new URLSearchParams(this.pendingParams));
        return;
      }
      this.handleRemoval(event);
    }

    handleFilterActions(event) {
      if (this.handleRemoval(event)) return;
      if (event.target.closest('.facets__retry')) {
        this.applyFilters(new URLSearchParams(this.pendingParams));
        return;
      }
      const more = event.target.closest('.js-show-more');
      if (more) {
        const filter = more.closest('.filter');
        const firstExtra = filter.querySelector('li.js-hidden input:not([disabled])');
        filter.querySelectorAll('li.js-hidden').forEach((item) => item.classList.remove('js-hidden'));
        this.expanded.add(filter.id);
        more.remove();
        firstExtra?.focus({ preventScroll: true });
      }
    }

    handleRemoval(event) {
      const link = event.target.closest('[data-facet-remove], [data-facet-clear-all]');
      if (!link || !this.scope.contains(link)) return false;
      event.preventDefault();
      const params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith('filter.') || key === 'page') params.delete(key);
      });
      if (!link.hasAttribute('data-facet-clear-all')) {
        new URL(link.href, window.location.href).searchParams.forEach((value, key) => {
          if (key.startsWith('filter.')) params.append(key, value);
        });
      }
      if (this.sortSelect) params.set('sort_by', this.sortSelect.value);
      this.applyFilters(params);
      return true;
    }

    formParams() {
      // Preserve search terms, collection views and other non-filter query values.
      const params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') params.delete(key);
      });
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') {
          if (!key.startsWith('filter.')) params.delete(key);
          params.append(key, value);
        }
      });
      return params;
    }

    handleInput(event) {
      const isSort = event.target === this.sortSelect;
      const isFilter = this.form.contains(event.target);
      if (!isSort && !isFilter) return;
      // Checkboxes update on input; prices update after their own range controller.
      const isPrice = event.target.closest('price-range');
      if (isSort && event.type !== 'change') return;
      if (!isSort && !isPrice && event.type !== 'input') return;

      clearTimeout(this.filterChangeTimeout);
      this.requestVersion += 1;
      this.request?.abort();
      this.failed = false;
      this.setBusy(true);
      this.filterChangeTimeout = setTimeout(() => {
        this.applyFilters(this.formParams());
      }, isPrice ? 550 : 200);
    }

    setBusy(busy) {
      if (busy && !this.busy) this.pagination?.controller?.abort();
      this.busy = busy;
      this.results?.classList.toggle('is-loading', busy);
      this.results?.setAttribute('aria-busy', String(busy));
      // Keep the permanent last focus target available while results update.
      this.resultsButton.setAttribute('aria-disabled', String(busy || !!this.failed));
      this.resultsButton.classList.toggle('is-loading', busy);
      this.updateErrorVisibility();
      if (this.pagination) this.pagination.dataset.pauseInfiniteScroll = String(busy);
    }

    updateErrorVisibility() {
      const open = this.hasAttribute('open');
      this.error.hidden = !this.failed || !open;
      this.retryButton.hidden = !this.failed || !open;
      if (this.toolbarError) this.toolbarError.hidden = !this.failed || open;
    }

    updateClearLinks() {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
      });
      if (this.sortSelect) url.searchParams.set('sort_by', this.sortSelect.value);
      this.scope.querySelectorAll('[data-facet-clear-all]').forEach((link) => {
        link.href = url.pathname + url.search;
      });
    }

    async applyFilters(params, updateHistory = true) {
      clearTimeout(this.filterChangeTimeout);
      const version = ++this.requestVersion;
      this.request?.abort();
      this.request = new AbortController();
      this.failed = false;
      this.pendingParams = params.toString();
      this.setBusy(true);
      const activeElement = document.activeElement;
      const activeId = activeElement?.id;
      const focusWasInside = this.contains(activeElement) || this.chips?.contains(activeElement);
      const contentScroll = this.content.scrollTop;
      const openFilters = new Map(Array.from(this.form.querySelectorAll('details')).map((item) => [item.id, item.open]));
      const fetchParams = new URLSearchParams(params);
      if (this.form.dataset.filterSectionId) fetchParams.set('section_id', this.form.dataset.filterSectionId);

      try {
        const response = await fetch(window.location.pathname + '?' + fetchParams, { signal: this.request.signal });
        if (!response.ok) throw new Error('Filter request failed: ' + response.status);
        const markup = document.createElement('template');
        markup.innerHTML = await response.text();
        if (version !== this.requestVersion || !this.isConnected) return;
        const nextForm = markup.content.querySelector('#facets');
        const nextResults = markup.content.querySelector('#filter-results');
        const nextCount = markup.content.querySelector('.products-count');
        const nextResultsButton = markup.content.querySelector('.facets__show-results');
        const nextClear = markup.content.querySelector('.drawer__footer [data-facet-clear-all]');
        if (!nextForm || !nextResults || !nextCount || !nextResultsButton || !nextClear) throw new Error('Incomplete filter response');

        nextForm.querySelectorAll('details').forEach((item) => {
          if (openFilters.has(item.id)) item.open = openFilters.get(item.id);
          if (this.expanded.has(item.id)) {
            item.querySelectorAll('li.js-hidden').forEach((li) => li.classList.remove('js-hidden'));
            item.querySelector('.js-show-more')?.remove();
          }
        });
        this.form.innerHTML = nextForm.innerHTML;
        this.results.innerHTML = nextResults.innerHTML;
        const count = this.toolbar?.querySelector('.products-count');
        if (count) {
          count.textContent = nextCount.textContent;
          count.dataset.resultsCount = nextCount.dataset.resultsCount;
        }
        const container = this.scope.querySelector('[data-num-results]');
        if (container) container.dataset.numResults = nextCount.dataset.resultsCount;
        const nextChips = markup.content.querySelector('#products-active-filters');
        if (this.chips && nextChips) {
          this.chips.innerHTML = nextChips.innerHTML;
          this.chips.hidden = nextChips.hidden;
        }
        const badge = this.toolbar?.querySelector('[data-filter-count]');
        const nextBadge = markup.content.querySelector('[data-filter-count]');
        if (badge && nextBadge) {
          badge.textContent = nextBadge.textContent;
          badge.hidden = nextBadge.hidden;
        }
        this.resultsButton.textContent = nextResultsButton.textContent;
        const clear = this.querySelector('.drawer__footer [data-facet-clear-all]');
        clear.hidden = nextClear.hidden;
        const nextSort = markup.content.querySelector('#products-sort-by');
        if (this.sortSelect && nextSort) this.sortSelect.value = nextSort.value;

        const nextUrl = window.location.pathname + (params.size ? '?' + params : '');
        if (updateHistory === 'replace') {
          window.history.replaceState({ searchParams: params.toString() }, '', nextUrl);
        } else if (updateHistory && nextUrl !== window.location.pathname + window.location.search) {
          window.history.pushState({ searchParams: params.toString() }, '', nextUrl);
        }
        this.updateClearLinks();
        this.pagination?.reload?.();
        window.ApProductCard?.init(this.results);
        this.content.scrollTop = contentScroll;
        if (this.hasAttribute('open')) {
          const target = (activeId && this.querySelector('[id="' + CSS.escape(activeId) + '"]')) || this.querySelector('.drawer__close-btn');
          trapFocus(this, target);
          this.content.scrollTop = contentScroll;
        } else if (focusWasInside) {
          (this.toolbar?.querySelector('.products-toolbar__filter-btn') || this.sortSelect)?.focus({ preventScroll: true });
        }
        document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
      } catch (error) {
        if (version !== this.requestVersion || error.name === 'AbortError') return;
        this.failed = true;
      } finally {
        if (version === this.requestVersion && this.isConnected) this.setBusy(false);
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);
}
